import { P } from 'components'
import { useFetchTransactionItemDetailQuery } from 'features/private-views/dashboard/dashboardApiSlice'
import { capitalizeEveryWord, PMBFormatDate } from 'helpers'
import { useState } from 'react'
import { ChevronDown, ChevronUp, Info, Package, RotateCcw } from 'react-feather'
import {
  Button,
  Col,
  Collapse,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap'
import colors from 'Theme/colors'
import { formatDatePMBV2 } from 'utils/formatDate'
import { newFormatThousandSeparator } from 'utils/formatNumber'


const isGroupNode = (node) =>
  Boolean(node?.groupLabel && Array.isArray(node?.items))

const flattenItems = (items) => {
  if (!Array.isArray(items)) return []
  const result = []
  for (const node of items) {
    if (isGroupNode(node)) {
      result.push(...flattenItems(node.items))
    } else {
      result.push(node)
    }
  }
  return result
}

const ModalOrderItemDetail = ({ isOpen, toggle, transaction, type, hidePrice: hidePriceProp }) => {
  const [expandedGroups, setExpandedGroups] = useState({})
  const [showAllAdjustedSummary, setShowAllAdjustedSummary] = useState(false)
  const hidePrice = hidePriceProp ?? type === 'sellerfills'

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }))
  }

  const escrowpaymentId =
    transaction?.escrowpaymentId || transaction?.escrowPaymentId
  const tradeshortcutId = !escrowpaymentId
    ? transaction?.tradeshortcutId ||
    transaction?.tradeShortcutId ||
    transaction?.id
    : undefined

  const { data: responseData, isLoading } =
    useFetchTransactionItemDetailQuery(
      {
        escrowpaymentId,
        tradeshortcutId,
        type: type || 'order',
      },
      { skip: !isOpen || !type || (!escrowpaymentId && !tradeshortcutId) },
    )

  if (!transaction) return null

  const isOrderType = type === 'order'
  const modalTitle = isOrderType
    ? 'Permintaan Pesanan Awal'
    : 'Hasil Pemenuhan Pesanan'

  const rawItems =
    responseData?.itemDetail ||
    responseData?.items ||
    (Array.isArray(responseData) ? responseData : null)

  const itemDetail = rawItems || transaction?.itemDetail || []
  const allLeafItems = flattenItems(itemDetail)

  // Detect adjustment
  const hasAnyAdjustment = allLeafItems.some((item) => item?.adjustmentitem)

  const adjustedValueColor = '#f04438'
  const previousValueColor = '#667085'

  const fulfillmentDate =
    transaction?.history?.sellerFillCreatedDate ||
    transaction?.history?.fulfilledDate ||
    transaction?.history?.fulfilmentDate ||
    transaction?.history?.fulfillmentDate ||
    transaction?.history?.sellerFulfillDate ||
    transaction?.history?.createdAt ||
    transaction?.fulfilledAt ||
    transaction?.createdAt

  const dateLabel = isOrderType ? 'Tanggal Pesanan' : 'Waktu Pemenuhan'
  const dateValue = isOrderType
    ? transaction?.history?.createdDate || transaction?.createdAt
    : fulfillmentDate

  const orderNo =
    transaction?.orderDetail?.orderNo ||
    transaction?.orderDetail?.poNumber ||
    transaction?.noTrx ||
    transaction?.id ||
    '-'
  const buyerName =
    transaction?.picBuyerDetail?.name ||
    transaction?.picBuyerDetail?.fullName ||
    transaction?.buyerDetail?.fullName ||
    '-'
  const sellerName =
    transaction?.picSellerDetail?.name ||
    transaction?.picSellerDetail?.fullName ||
    transaction?.sellerDetail?.fullName ||
    '-'

  // Calculate transaction total
  const transactionSum = allLeafItems.reduce((sum, item) => {
    if (hasAnyAdjustment && item?.adjustmentitem) {
      return (
        sum + Number(item.adjustmentitem.newTotalPrice ?? 0)
      )
    }
    return (
      sum +
      Number(
        item?.totalPrice ?? (item?.amount ?? 0) * (item?.price ?? 0),
      )
    )
  }, 0)

  // Calculate original total
  const originalTotal = allLeafItems.reduce((sum, item) => {
    return (
      sum +
      Number(
        item?.totalPrice ?? (item?.amount ?? 0) * (item?.price ?? 0),
      )
    )
  }, 0)

  // Unit summary
  const unitSummary = allLeafItems.reduce((acc, item) => {
    const unitRaw = item?.unit ?? ''
    const unit = String(unitRaw).toLowerCase()
    if (!unit) return acc

    if (!acc[unit]) {
      acc[unit] = {
        unit,
        inventoryCount: 0,
        totalAmount: 0,
        newTotalAmount: 0,
      }
    }

    acc[unit].inventoryCount += 1
    acc[unit].totalAmount += Number(item?.amount ?? 0)

    if (hasAnyAdjustment && item?.adjustmentitem) {
      acc[unit].newTotalAmount += Number(
        item.adjustmentitem.newAmount ?? 0,
      )
    } else {
      acc[unit].newTotalAmount += Number(item?.amount ?? 0)
    }

    return acc
  }, {})

  const unitSummaryArray = Object.values(unitSummary || {})

  // List of items that have adjustments / differences
  const adjustedItemsList = allLeafItems.filter((item) => {
    const adj = item?.adjustmentitem
    if (!hasAnyAdjustment || !adj) return false
    const origAmt = item?.amount
    const displayAmt = adj.newAmount
    const origPrice = item?.price
    const displayPrice = adj.newPrice
    const origTotal = item?.totalPrice ?? (origAmt ?? 0) * (origPrice ?? 0)
    const displayTotal = adj.newTotalPrice ?? (displayAmt ?? 0) * (displayPrice ?? 0)
    return (
      origAmt !== displayAmt ||
      origPrice !== displayPrice ||
      origTotal !== displayTotal
    )
  })

  // Calculate total price difference across all adjusted items
  const totalDiffAmount = adjustedItemsList.reduce((sum, item) => {
    const adj = item?.adjustmentitem
    const origAmt = Number(item?.amount ?? 0)
    const origPrice = Number(item?.price ?? 0)
    const origTotal = Number(item?.totalPrice ?? origAmt * origPrice)
    const displayTotal = Number(
      adj?.newTotalPrice ?? Number(adj?.newAmount ?? 0) * Number(adj?.newPrice ?? 0)
    )
    return sum + (displayTotal - origTotal)
  }, 0)

  // ============================================================
  // RENDER: Individual leaf item (Template seragam DetailOrderSection)
  // ============================================================
  const renderLeafItem = (item, key) => {
    const adj = item?.adjustmentitem
    const hasAdj = hasAnyAdjustment && Boolean(adj)

    const nameItem = item?.nameItem ?? '-'
    const unit = item?.unit ?? ''
    const inventoryOwnerCode = item?.inventoryOwnerCode || item?.ownerCode
    const lotCode = item?.lotCode
    const bundle = item?.bundle

    const displayAmount = hasAdj ? adj.newAmount : item?.amount
    const displayPrice = hasAdj ? adj.newPrice : item?.price
    const displayTotal = hasAdj
      ? adj.newTotalPrice
      : item?.totalPrice ?? (item?.amount ?? 0) * (item?.price ?? 0)

    const originalAmount = item?.amount
    const originalPrice = item?.price
    const originalTotalPrice =
      item?.totalPrice ?? (originalAmount ?? 0) * (originalPrice ?? 0)

    const showOriginal =
      hasAdj &&
      (originalAmount !== displayAmount ||
        originalPrice !== displayPrice ||
        originalTotalPrice !== displayTotal)

    const diffAmount = Number(displayAmount ?? 0) - Number(originalAmount ?? 0)
    const diffTotal = Number(displayTotal ?? 0) - Number(originalTotalPrice ?? 0)

    return (
      <div
        key={key}
        className="mb-2 bg-white p-2 rounded border border-gray-200"
        data-testid={`modal-order-item-${item?.orderitemId ?? key}`}
      >
        {/* Item name */}
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span className="fw-semibold">{nameItem}</span>
          <div className="d-flex align-items-center gap-1">
            {bundle !== undefined && bundle !== null && Number(bundle) > 0 && (
              <span className="badge bg-primary">{bundle} bundle</span>
            )}
          </div>
        </div>

        {/* Amount x Price = Total */}
        <div className="d-flex justify-content-between align-items-center">
          <P
            size="regular"
            className="fw-semibold mb-0"
            style={{ color: hasAdj ? adjustedValueColor : undefined }}
          >
            {newFormatThousandSeparator(Number(displayAmount ?? 0))}{' '}
            <span className="text-capitalize">{unit}</span>
            {!hidePrice && (
              <>
                {' x Rp'}
                {newFormatThousandSeparator(Number(displayPrice ?? 0), 0)}
              </>
            )}
          </P>
          {!hidePrice && (
            <P
              className={`fw-semibold mb-0 ${hasAdj ? '' : 'text-primary'}`}
              style={{ color: hasAdj ? adjustedValueColor : undefined }}
            >
              Rp{newFormatThousandSeparator(Number(displayTotal ?? 0), 0)}
            </P>
          )}
        </div>

        {/* Original values if adjusted */}
        {showOriginal && (
          <div
            className="d-flex justify-content-between align-items-center text-muted mt-1"
            style={{ fontSize: '0.8rem' }}
          >
            <span>
              <span
                className="badge bg-light text-secondary border me-1 fw-normal"
                style={{ fontSize: '0.68rem', padding: '2px 5px' }}
              >
                Semula
              </span>
              <span className="text-decoration-line-through">
                {newFormatThousandSeparator(Number(originalAmount ?? 0))}{' '}
                <span className="text-capitalize">{unit}</span>
                {!hidePrice && (
                  <>
                    {' x Rp'}
                    {newFormatThousandSeparator(Number(originalPrice ?? 0), 0)}
                  </>
                )}
              </span>
            </span>
            {!hidePrice && (
              <span className="text-decoration-line-through">
                Rp{newFormatThousandSeparator(Number(originalTotalPrice ?? 0), 0)}
              </span>
            )}
          </div>
        )}

        {/* Inventory Code & Lot ID */}
        <div
          className="text-muted mt-1 d-flex flex-column"
          style={{ fontSize: '0.8em', marginTop: '2px' }}
        >
          <span>Kode Inventori: {inventoryOwnerCode ?? '-'}</span>
          <span>Lot ID: {lotCode ?? '-'}</span>
          {(item?.projectLabel || item?.projectName || item?.projectId) && (
            <span>
              Project:{' '}
              {item?.projectLabel ||
                item?.projectName ||
                `Project #${item.projectId}`}
            </span>
          )}
        </div>

        {/* Adjustment detail info */}
        {hasAdj && Boolean(adj) && (
          <div
            className="mt-2 p-2 rounded"
            style={{
              backgroundColor: '#F9FAFB',
              border: '1px solid #EAECF0',
            }}
          >
            <div
              className="d-flex align-items-center justify-content-between mb-2 pb-1 border-bottom"
              style={{ borderColor: '#EAECF0' }}
            >
              <span
                className="fw-semibold text-dark d-flex align-items-center gap-1"
                style={{ fontSize: '0.75rem' }}
              >
                <RotateCcw size={13} className="text-warning" /> Detail Perubahan
              </span>
              <span
                className="badge"
                style={{
                  backgroundColor:
                    adj?.editType?.toUpperCase() === 'RETURN' ||
                      (!adj?.editType && (diffAmount < 0 || adj?.causeOfChange))
                      ? '#FEE4E2'
                      : '#FEF0C7',
                  color:
                    adj?.editType?.toUpperCase() === 'RETURN' ||
                      (!adj?.editType && (diffAmount < 0 || adj?.causeOfChange))
                      ? '#B42318'
                      : '#B54708',
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  padding: '3px 8px',
                  borderRadius: '12px',
                }}
              >
                {adj?.editType && adj?.editType !== ''
                  ? adj?.editType?.toUpperCase() === 'ADJUSTMENT'
                    ? 'Penyesuaian'
                    : capitalizeEveryWord(adj.editType)
                  : diffAmount < 0 || adj?.causeOfChange
                    ? 'Return'
                    : 'Penyesuaian'}
              </span>
            </div>

            <Row className="g-2" style={{ fontSize: '0.75rem' }}>
              <Col xs="6">
                <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                  Penyebab
                </span>
                <span className="fw-semibold text-dark">
                  {adj.causeOfChange || '-'}
                </span>
              </Col>
              <Col xs="6">
                <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                  Catatan
                </span>
                <span className="fw-semibold text-dark">
                  {adj.reason && adj.reason !== '' ? adj.reason : '-'}
                </span>
              </Col>
              {showOriginal && (
                <>
                  <Col xs="6" className="mt-1">
                    <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                      Selisih Kuantitas
                    </span>
                    <span className={`fw-semibold ${diffAmount < 0 ? 'text-danger' : diffAmount > 0 ? 'text-success' : 'text-dark'}`}>
                      {diffAmount < 0
                        ? `Diretur ${newFormatThousandSeparator(Math.abs(diffAmount))} ${unit}`
                        : diffAmount > 0
                          ? `+${newFormatThousandSeparator(diffAmount)} ${unit}`
                          : '0'}
                    </span>
                  </Col>
                  {!hidePrice && (
                    <Col xs="6" className="mt-1">
                      <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                        Selisih Harga Total
                      </span>
                      <span className={`fw-semibold ${diffTotal < 0 ? 'text-danger' : diffTotal > 0 ? 'text-success' : 'text-dark'}`}>
                        {diffTotal < 0
                          ? `-Rp${newFormatThousandSeparator(Math.abs(diffTotal), 0)}`
                          : diffTotal > 0
                            ? `+Rp${newFormatThousandSeparator(diffTotal, 0)}`
                            : 'Rp0'}
                      </span>
                    </Col>
                  )}
                </>
              )}
              {adj?.checkTime && (
                <>
                  <Col xs="6" className="mt-1">
                    <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                      Diperiksa Oleh
                    </span>
                    <span className="text-dark">{adj?.checkUserName ?? '-'}</span>
                  </Col>
                  <Col xs="6" className="mt-1">
                    <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                      Waktu Perubahan
                    </span>
                    <span className="text-dark">
                      {PMBFormatDate(adj.checkTime)}
                    </span>
                  </Col>
                </>
              )}
            </Row>
          </div>
        )}
      </div>
    )
  }


  const renderGroupNode = (node, depth = 0, parentPath = '') => {
    const groupKey = `${parentPath}_${node.groupLabel}_${depth}`
    const isOpen = expandedGroups[groupKey] === true

    const groupLeafItems = flattenItems(node.items)
    const leafCount = groupLeafItems.length
    const groupTotal = groupLeafItems.reduce((sum, item) => {
      if (hasAnyAdjustment && item?.adjustmentitem) {
        return (
          sum + Number(item.adjustmentitem.newTotalPrice ?? 0)
        )
      }
      return (
        sum +
        Number(
          item?.totalPrice ?? (item?.amount ?? 0) * (item?.price ?? 0),
        )
      )
    }, 0)

    // Calculate group per-unit breakdown
    const groupUnitSummary = groupLeafItems.reduce((acc, item) => {
      const unitRaw = item?.unit ?? ''
      const unit = String(unitRaw).trim().toLowerCase()
      if (!unit) return acc

      if (!acc[unit]) {
        acc[unit] = {
          unit: unitRaw,
          totalAmount: 0,
          newTotalAmount: 0,
        }
      }

      acc[unit].totalAmount += Number(item?.amount ?? 0)

      const adj = item?.adjustmentitem
      if (hasAnyAdjustment && adj) {
        acc[unit].newTotalAmount += Number(adj.newAmount ?? 0)
      } else {
        acc[unit].newTotalAmount += Number(item?.amount ?? 0)
      }

      return acc
    }, {})

    const groupUnitTexts = Object.values(groupUnitSummary).map((u) => {
      const count = hasAnyAdjustment ? u.newTotalAmount : u.totalAmount
      return `${newFormatThousandSeparator(count, 3)} ${u.unit}`
    })

    const groupBundleCount = groupLeafItems.reduce(
      (sum, item) => sum + (Number(item?.bundle) || 0),
      0,
    )

    const groupMetaTexts = [`${leafCount} barang`]
    if (groupBundleCount > 0) {
      groupMetaTexts.push(`${newFormatThousandSeparator(groupBundleCount, 0)} bundle`)
    }

    const groupSummaryText =
      groupUnitTexts.length > 0
        ? `${groupUnitTexts.join(', ')} (${groupMetaTexts.join(' dan ')})`
        : `Terdiri dari ${groupMetaTexts.join(' dan ')}`

    return (
      <div
        key={groupKey}
        className={`mb-2 ${depth === 0 ? 'bg-light' : 'bg-white'} p-2 rounded border border-gray-200`}
        style={{ marginLeft: depth > 0 ? `${Math.min(depth * 8, 24)}px` : 0 }}
      >
        {/* Group header */}
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div className="d-flex align-items-center gap-2">
            <Package size={16} className="text-primary" />
            <span className="fw-bold">{node.groupLabel}</span>

          </div>

          <Button
            onClick={() => toggleGroup(groupKey)}
            className="btn btn-sm btn-link p-0 text-decoration-none d-flex align-items-center gap-1 btn-hover-none"
          >
            <span
              className="text-primary fw-medium"
              style={{ fontSize: '12px' }}
            >
              {isOpen ? 'Tutup' : `Lihat Isi (${leafCount})`}
            </span>
            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </Button>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-2 mb-1">
          <P size="small" color="gray" className="mb-0">
            {isOpen ? '' : groupSummaryText}
          </P>
          {!hidePrice && (
            <P bold className="text-primary mb-0">
              Rp{newFormatThousandSeparator(Number(groupTotal), 0)}
            </P>
          )}
        </div>

        {/* Group content (collapsible) */}
        <Collapse isOpen={isOpen}>
          <div className="pt-2">
            {node.items.map((child, idx) => {
              const childPath = `${groupKey}_${idx}`
              if (isGroupNode(child)) {
                return renderGroupNode(child, depth + 1, childPath)
              }
              return renderLeafItem(child, childPath)
            })}
          </div>
        </Collapse>
      </div>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="lg"
      data-testid="modal-order-item-detail"
    >
      <ModalHeader toggle={toggle} className="border-bottom pb-3">
        <span className="fw-bold">{modalTitle}</span>
      </ModalHeader>

      <ModalBody className="p-4">
        {/* Order Info */}
        <div className="bg-light p-3 rounded mb-4">
          <div className="row g-2 text-sm">
            <div className="col-6 col-md-3">
              <span className="text-muted d-block">No. Transaksi</span>
              <span className="fw-semibold text-break">{orderNo}</span>
            </div>
            <div className="col-6 col-md-3">
              <span className="text-muted d-block">{dateLabel}</span>
              <span className="fw-semibold">
                {dateValue ? formatDatePMBV2(dateValue) : '-'}
              </span>
            </div>
            <div className="col-6 col-md-3">
              <span className="text-muted d-block">Pembeli</span>
              <span className="fw-semibold">{buyerName}</span>
            </div>
            <div className="col-6 col-md-3">
              <span className="text-muted d-block">Penjual</span>
              <span className="fw-semibold">{sellerName}</span>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <P className="mt-2 text-muted">Memuat data item...</P>
          </div>
        ) : (
          <>
            {/* SUMMARY CARD FOR RETURNED / ADJUSTED ITEMS */}
            {adjustedItemsList.length > 0 && (
              <div
                className="mb-3 p-3 rounded border"
                style={{
                  backgroundColor: '#FFFAEB',
                  borderColor: '#FEDF89',
                }}
              >
                <div
                  className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom"
                  style={{ borderColor: '#FEF0C7' }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <RotateCcw size={16} style={{ color: '#B54708' }} />
                    <span
                      className="fw-bold"
                      style={{ color: '#B54708', fontSize: '0.85rem' }}
                    >
                      Ringkasan Retur & Penyesuaian ({adjustedItemsList.length} Barang)
                    </span>
                  </div>
                  {!hidePrice && (
                    <span
                      className="fw-bold"
                      style={{
                        color: totalDiffAmount < 0 ? '#B42318' : '#027948',
                        fontSize: '0.85rem',
                      }}
                    >
                      Total Selisih:{' '}
                      {totalDiffAmount < 0
                        ? `-Rp${newFormatThousandSeparator(Math.abs(totalDiffAmount), 0)}`
                        : `+Rp${newFormatThousandSeparator(totalDiffAmount, 0)}`}
                    </span>
                  )}
                </div>

                <div
                  className="d-flex flex-column gap-2"
                  style={{
                    fontSize: '0.8rem',
                    maxHeight: showAllAdjustedSummary ? '320px' : 'none',
                    overflowY: showAllAdjustedSummary ? 'auto' : 'visible',
                    paddingRight: showAllAdjustedSummary ? '4px' : '0px',
                  }}
                >
                  {(showAllAdjustedSummary
                    ? adjustedItemsList
                    : adjustedItemsList.slice(0, 3)
                  ).map((item, idx) => {
                    const adj = item?.adjustmentitem
                    const unit = item?.unit ?? ''
                    const origAmt = Number(item?.amount ?? 0)
                    const newAmt = Number(adj?.newAmount ?? origAmt)
                    const diffAmt = newAmt - origAmt
                    const origTot = Number(
                      item?.totalPrice ?? origAmt * Number(item?.price ?? 0),
                    )
                    const newTot = Number(
                      adj?.newTotalPrice ?? newAmt * Number(adj?.newPrice ?? 0),
                    )
                    const diffTot = newTot - origTot

                    return (
                      <div
                        key={idx}
                        className="d-flex justify-content-between align-items-center bg-white p-2 rounded border"
                        style={{ borderColor: '#EAECF0' }}
                      >
                        <div>
                          <span
                            className="fw-semibold text-dark"
                            style={{ fontSize: '0.8rem' }}
                          >
                            {item?.nameItem ?? '-'}
                          </span>
                          <div
                            className="text-muted"
                            style={{ fontSize: '0.75rem' }}
                          >
                            Kuantitas: {newFormatThousandSeparator(origAmt)} {unit}{' '}
                            ➔ {newFormatThousandSeparator(newAmt)} {unit}
                          </div>
                        </div>
                        <div className="text-end">
                          {diffAmt !== 0 && (
                            <span
                              className={`badge ${diffAmt < 0 ? 'bg-danger-subtle text-danger border border-danger-subtle' : 'bg-success-subtle text-success border border-success-subtle'} me-1 fw-semibold`}
                              style={{ fontSize: '0.7rem' }}
                            >
                              {diffAmt < 0
                                ? `Diretur ${newFormatThousandSeparator(Math.abs(diffAmt))} ${unit}`
                                : `+${newFormatThousandSeparator(diffAmt)} ${unit}`}
                            </span>
                          )}
                          {!hidePrice && (
                            <div
                              className={`fw-semibold ${diffTot < 0 ? 'text-danger' : diffTot > 0 ? 'text-success' : 'text-dark'}`}
                              style={{ fontSize: '0.8rem' }}
                            >
                              {diffTot < 0
                                ? `-Rp${newFormatThousandSeparator(Math.abs(diffTot), 0)}`
                                : `+Rp${newFormatThousandSeparator(diffTot, 0)}`}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>

                {adjustedItemsList.length > 3 && (
                  <div
                    className="text-center mt-2 pt-2 border-top"
                    style={{ borderColor: '#FEF0C7' }}
                  >
                    <Button
                      color="link"
                      size="sm"
                      className="p-0 text-decoration-none fw-semibold"
                      style={{ color: '#B54708', fontSize: '0.78rem' }}
                      onClick={() => setShowAllAdjustedSummary((prev) => !prev)}
                    >
                      {showAllAdjustedSummary
                        ? 'Tampilkan Lebih Sedikit ▲'
                        : `Lihat Selengkapnya (${adjustedItemsList.length - 3} barang lagi) ▼`}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* ITEMS LIST (Nested groups & leaf items) */}
            <div className="mb-3">
              {itemDetail.length > 0 ? (
                itemDetail.map((node, idx) => {
                  if (isGroupNode(node)) {
                    return renderGroupNode(node, 0, `root_${idx}`)
                  }
                  return renderLeafItem(node, `item_${idx}`)
                })
              ) : (
                <div className="text-center text-muted py-4 border rounded bg-light">
                  Tidak ada item pesanan.
                </div>
              )}
            </div>

            {/* Total Summary */}
            {!hidePrice && (
              <>
                <hr />
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <P size="medium" bold>
                    Total
                  </P>
                  <strong>
                    {hasAnyAdjustment && transactionSum !== originalTotal ? (
                      <div className="text-end">
                        <P size="medium" bold className="text-primary">
                          Rp
                          {newFormatThousandSeparator(
                            Number(transactionSum ?? 0),
                            0,
                          )}
                        </P>
                        <P style={{ color: previousValueColor, fontSize: '0.85rem' }}>
                          (Rp
                          {newFormatThousandSeparator(Number(originalTotal), 0)})
                        </P>
                      </div>
                    ) : (
                      <P bold className="text-primary fs-5 mb-0">
                        Rp
                        {newFormatThousandSeparator(
                          Number(transactionSum ?? 0),
                          0,
                        )}
                      </P>
                    )}
                  </strong>
                </div>
              </>
            )}

            {allLeafItems?.length > 1 &&
              (() => {
                const totalAmountSum = allLeafItems.reduce((sum, item) => {
                  const adj = item?.adjustmentitem
                  const amt =
                    hasAnyAdjustment && adj
                      ? Number(adj.newAmount ?? 0)
                      : Number(item?.amount ?? 0)
                  return sum + amt
                }, 0)

                const uniqueUnits = Array.from(
                  new Set(
                    allLeafItems
                      .map((i) => String(i?.unit || '').trim().toLowerCase())
                      .filter(Boolean),
                  ),
                )

                const isMonoUnit = uniqueUnits.length === 1
                const monoUnitName =
                  allLeafItems.find((i) => Boolean(i?.unit?.trim()))?.unit ||
                  'qty'
                const displayUnitLabel = isMonoUnit ? monoUnitName : 'qty'

                const uniqueCommodities = new Set(
                  allLeafItems
                    .map((i) => String(i?.nameItem || '').trim().toLowerCase())
                    .filter(Boolean),
                )
                const totalCommoditiesCount =
                  uniqueCommodities.size || allLeafItems.length

                const totalBundle = allLeafItems.reduce(
                  (sum, item) => sum + (Number(item?.bundle) || 0),
                  0,
                )

                const metaTexts = [`${totalCommoditiesCount} jenis barang`]
                if (totalBundle > 0) {
                  metaTexts.push(
                    `${newFormatThousandSeparator(totalBundle, 0)} bundle`,
                  )
                }

                const formattedTotalAmount = newFormatThousandSeparator(
                  totalAmountSum,
                  3,
                )
                const metaText = metaTexts.join(', ')

                return (
                  <div
                    className="mb-3 rounded"
                    style={{
                      padding: '12px 16px',
                      backgroundColor: colors.primary[400],
                      color: 'white',
                    }}
                  >
                    <P bold style={{ color: 'white', marginBottom: 0 }}>
                      {`Total: ${formattedTotalAmount} ${displayUnitLabel} (${metaText})`}
                    </P>
                  </div>
                )
              })()}
          </>
        )}

        <div className="d-flex justify-content-end mt-4">
          <Button outline color="secondary" onClick={toggle} className="px-4">
            Tutup
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ModalOrderItemDetail
